/**
 * WHERE THE RVS WENT, AND WHEN THEY ARE COMING BACK.
 *
 * Anthony, 2026-09-28: "Map feature should show where RVs went to and when
 * they are returning." Every trip Out or Returning is a pin at its
 * destination, labelled with the RV, the renter and "back Oct 14", or "due
 * back today", or in red "overdue 2 days". Booked trips we are delivering
 * can be switched on too, because the question before a delivery run is
 * the same map. The yard is the home pin.
 *
 * ── THE SAME MAP AS THE DAY MAP ─────────────────────────────────────────
 *
 * Leaflet 1.9.4 off cdnjs, OpenStreetMap tiles with their credit, and the
 * daymap's own card, list and dot classes. Imported from src/pages/daymap.ts
 * rather than copied, so there is one map library, one tile source and one
 * answer to "why not CARTO" in the product.
 *
 * ── WHERE A PIN COMES FROM ─────────────────────────────────────────────
 *
 * destination_lat and destination_lng on the trip (migration 0185). The
 * server fills them when the destination names a town it can place from
 * the Census list (server/place/anywhere.ts, no network call, no key), and
 * a person can type a point on the trip page when it cannot: a campground
 * name alone places nowhere, and guessing is how Hemet once landed in
 * Tampa. A trip with no point is listed under the map as "no location
 * yet", never dropped.
 */

import { esc, mount } from "../ui/dom.ts";
import { rentalsApi } from "../data/rentals.ts";
import { rentalsDown } from "./rentals.ts";
import { backLine, isOverdue, shortDay, unitName, type Stage, type Unit } from "../domain/rental.ts";
import { loadLeaflet, tokenColor, TILES, TILE_CREDIT, type Leaflet, type LMarker } from "./daymap.ts";

type MapTrip = {
  id: string; stage: Stage; start_date: string | null; end_date: string | null; renter_name: string | null;
  renter_phone: string | null; destination: string | null; destination_lat: number | null; destination_lng: number | null;
  delivery: boolean; delivery_address: string | null; unit: Pick<Unit, "year" | "make" | "model" | "description"> | null;
};
type MapAnswer = { today: string; yard: { lat: number; lng: number } | null; yard_label: string | null; trips: MapTrip[] };

let withDeliveries = false;

export async function rentalMapPage(): Promise<void> {
  mount(`<div class="page page-wide">
    <header class="page-head"><h1>Where the RVs Are</h1>
      <p>Every RV out on a trip, at the place they said they were headed, and when it is due back.</p></header>
    <label class="check rhead-acts"><input type="checkbox" id="rm-del"${withDeliveries ? " checked" : ""}> Also show booked trips we are delivering</label>
    <div id="rm-host"><div class="card"><div class="loading">Reading&hellip;</div></div></div>
  </div>`);
  document.getElementById("rm-del")?.addEventListener("change", (e) => {
    withDeliveries = (e.currentTarget as HTMLInputElement).checked;
    void draw();
  });
  await draw();
}

async function draw(): Promise<void> {
  const host = document.getElementById("rm-host");
  if (!host) return;
  const res = await rentalsApi<MapAnswer>("/map");
  if (!res.ok) { host.innerHTML = `<div class="card">${rentalsDown(res.error, res.missing)}</div>`; return; }
  const { today, yard, yard_label: yardLabel } = res.data;
  const trips = res.data.trips.filter((t) => t.stage === "out" || t.stage === "returning"
    || (withDeliveries && t.delivery && (t.stage === "booked" || t.stage === "prep")));
  const placed = trips.filter((t) => t.destination_lat !== null && t.destination_lng !== null);
  const nowhere = trips.filter((t) => t.destination_lat === null || t.destination_lng === null);
  const line = (t: MapTrip) => t.stage === "booked" || t.stage === "prep"
    ? `delivering ${shortDay(t.start_date)}` : backLine(t.stage, t.end_date, today);
  const late = (t: MapTrip) => isOverdue(t.stage, t.end_date, today);
  /* --green2, not --green: on a dark desk wearing a navy brand, --green is
     a navy dot on a dark map (seen on WeRV's, 2026-09-28). --green2 is the
     lifted brand, bright on dark and deep on light. */
  const dot = (t: MapTrip) => late(t) ? "--red" : t.stage === "returning" || line(t) === "due back today" ? "--amber" : "--green2";

  const row = (t: MapTrip) => `<a class="daymap-row${t.destination_lat === null ? " off" : ""}" href="#/trip/${esc(t.id)}">
      <span class="daymap-dot" style="background:var(${dot(t)})"></span>
      <span class="daymap-who"><b>${esc(unitName(t.unit) || "No RV picked")}</b><i>${esc(t.renter_name || "No name yet")}</i></span>
      <span class="daymap-where${late(t) ? " rlate" : ""}">${esc(line(t))}<br>${
        t.destination_lat === null ? `<span class="daymap-nowhere">no location yet</span>` : esc(t.destination ?? "")}</span>
    </a>`;

  host.innerHTML = `<div class="card daymap-card rmap">
      <div class="card-head">On the road<span class="daymap-note">${trips.length} trip${trips.length === 1 ? "" : "s"}</span></div>
      <div class="daymap-shell">
        ${placed.length || yard ? `<div class="daymap-map" id="rm-canvas"></div>`
          : `<div class="daymap-blank">${trips.length ? "None of these trips has a place on the map yet." : "No RV is out right now."}</div>`}
        <div class="daymap-list">${trips.map(row).join("") || `<div class="daymap-blank sm">Nothing out.</div>`}</div>
      </div>
      <div class="daymap-foot">
        <span class="daymap-key"><span class="daymap-dot" style="background:var(--green2)"></span>out
          <span class="daymap-dot" style="background:var(--amber)"></span>due back today
          <span class="daymap-dot" style="background:var(--red)"></span>overdue
          <span class="daymap-dot" style="background:var(--purple)"></span>the yard</span>
        <span>Pins are the town they named, not a campsite.</span>
        ${nowhere.length ? `<span class="daymap-warn">${nowhere.length} with no location yet. Open the trip and type a map point.</span>` : ""}
      </div>
    </div>`;

  if (!placed.length && !yard) return;
  try { await loadLeaflet(); } catch (e) {
    const shell = host.querySelector(".daymap-map");
    if (shell) shell.innerHTML = `<div class="daymap-blank">${esc(e instanceof Error ? e.message : String(e))} The list is all of it.</div>`;
    return;
  }
  const L = (window as unknown as { L: Leaflet }).L;
  const canvas = document.getElementById("rm-canvas");
  if (!canvas) return;
  const map = L.map(canvas, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
  L.tileLayer(TILES, { attribution: TILE_CREDIT, maxZoom: 18 }).addTo(map);
  const marks: LMarker[] = [];
  if (yard) {
    marks.push(L.circleMarker([yard.lat, yard.lng], { radius: 10, weight: 3, color: tokenColor("--bg2"),
      fillColor: tokenColor("--purple"), fillOpacity: 1 }).addTo(map)
      .bindPopup(`<b>The yard</b><br>${esc(yardLabel ?? "")}`));
  }
  for (const t of placed) {
    const m = L.circleMarker([t.destination_lat as number, t.destination_lng as number], {
      radius: 8, weight: 2, color: tokenColor("--bg2"), fillColor: tokenColor(dot(t)), fillOpacity: 1,
    }).addTo(map);
    /* ALWAYS-ON LABELS ONLY WHERE THERE IS ROOM. On a phone three full
       labels ("2021 Winnebago Minnie Winnie 22M · Jordan Ellis · overdue
       2 days") ran off both edges and over each other, so under 640px the
       label is the renter's first name and when it is back, and the rest
       is a tap on the pin away. */
    const narrow = window.innerWidth < 640;
    m.bindTooltip(narrow
      ? `${esc((t.renter_name ?? "RV").split(" ")[0] ?? "RV")} &middot; ${esc(line(t))}`
      : `${esc(unitName(t.unit) || "RV")} &middot; ${esc(t.renter_name ?? "")} &middot; ${esc(line(t))}`,
      { permanent: true, direction: "top", offset: [0, -8] });
    m.bindPopup(`<b>${esc(t.renter_name || "No name yet")}</b><br>${esc(unitName(t.unit))}<br>${esc(t.destination ?? "")}<br>${
      esc(line(t))}<br><a href="#/trip/${esc(t.id)}">Open the trip &rarr;</a>`);
    marks.push(m);
  }
  map.fitBounds(L.featureGroup(marks).getBounds().pad(0.25), { maxZoom: 10 });
}
